import React from 'react';
import { Pressable, View, TouchableOpacity } from 'react-native';
import { Text } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { Calendar, MapPin, Heart, Users, MessageCircle } from 'lucide-react-native';
import { COLORS } from '@/constants/colors';
import { getStatusColor, getStatusText } from './utils';
import type { Announcement } from '@/data/announcementsData';

type AnnouncementCardProps = {
  announcement: Announcement;
  isDark: boolean;
  textColor: string;
  subtextColor: string;
  newApplicationsCount?: number;
  onPress?: () => void;
  onHeartPress?: () => void;
};

export const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  announcement,
  isDark,
  textColor,
  subtextColor,
  newApplicationsCount = 0,
  onPress,
  onHeartPress,
}) => {
  return (
    <Pressable onPress={onPress}>
      <YStack
        backgroundColor={isDark ? '#1C1C1C' : COLORS.WHITE}
        borderRadius={16}
        padding="$4"
        borderWidth={1}
        borderColor={isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
        space="$3"
      >
        {/* Category */}
        <Text fontSize={12} fontWeight="600" color={COLORS.PRIMARY} textTransform="uppercase">
          {announcement.category}
        </Text>

        {/* Title with new badge */}
        <XStack alignItems="center" gap="$2">
          <Text fontSize={18} fontWeight="700" color={textColor} numberOfLines={1} flex={1}>
            {announcement.title}
          </Text>
          {newApplicationsCount > 0 && (
            <XStack
              backgroundColor={COLORS.ERROR}
              paddingHorizontal="$2"
              paddingVertical={3}
              borderRadius={8}
            >
              <Text fontSize={11} fontWeight="600" color={COLORS.WHITE}>
                {newApplicationsCount} nowe
              </Text>
            </XStack>
          )}
        </XStack>

        {/* Description - 4 lines */}
        <Text fontSize={14} color={subtextColor} numberOfLines={4} lineHeight={20}>
          {announcement.description}
        </Text>

        {/* Date/Participants (left) and Location (right) */}
        <XStack justifyContent="space-between" alignItems="flex-start">
          {/* Left side - Date and Participants */}
          <YStack space="$2" flex={1}>
            {/* Date & Time */}
            <XStack alignItems="center" gap="$2">
              <Calendar size={16} color={subtextColor} />
              <Text fontSize={14} color={subtextColor}>
                {announcement.date}, {announcement.time}
              </Text>
            </XStack>

            {/* Participants */}
            <XStack alignItems="center" gap="$2">
              <Users size={16} color={subtextColor} />
              <Text fontSize={14} color={subtextColor}>
                {announcement.participants}-{announcement.maxParticipants}
              </Text>
            </XStack>
          </YStack>

          {/* Right side - Location */}
          <XStack alignItems="center" gap="$2" maxWidth="40%">
            <MapPin size={16} color={subtextColor} />
            <Text fontSize={14} color={subtextColor} numberOfLines={2}>
              {announcement.location}
            </Text>
          </XStack>
        </XStack>

        {/* Separator */}
        <View
          style={{
            height: 1,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          }}
        />

        {/* Bottom row: Likes/Comments (left) and Status (right) */}
        <XStack justifyContent="space-between" alignItems="center">
          {/* Left side - Likes and Comments */}
          <XStack alignItems="center" gap="$4">
            {/* Likes - clickable to view applications */}
            <TouchableOpacity 
              onPress={(e) => {
                e.stopPropagation();
                onHeartPress?.();
              }}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <XStack alignItems="center" gap="$1.5">
                <Heart size={16} color={COLORS.ERROR} fill={COLORS.ERROR} />
                <Text fontSize={14} fontWeight="600" color={textColor}>
                  {announcement.likes}
                </Text>
              </XStack>
            </TouchableOpacity>

            {/* Comments */}
            <XStack alignItems="center" gap="$1.5">
              <MessageCircle size={16} color={subtextColor} />
              <Text fontSize={14} fontWeight="600" color={textColor}>
                {announcement.comments}
              </Text>
            </XStack>
          </XStack>

          {/* Right side - Status Badge */}
          <XStack
            backgroundColor={getStatusColor(announcement.status)}
            paddingHorizontal="$3"
            paddingVertical="$1.5"
            borderRadius={8}
          >
            <Text fontSize={12} fontWeight="600" color={COLORS.WHITE}>
              {getStatusText(announcement.status)}
            </Text>
          </XStack>
        </XStack>
      </YStack>
    </Pressable>
  );
};

